import { Component, ChangeDetectionStrategy, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Base item interface for checklist items
 */
export interface ChecklistItem {
  id: number | string;
  label: string;
  disabled?: boolean;
}

/**
 * Group definition for grouped display
 */
export interface ChecklistGroup<T extends ChecklistItem = ChecklistItem> {
  id: number | string;
  label: string;
  items: T[];
}

/**
 * ChecklistPanelComponent
 *
 * A reusable checklist panel with:
 * - Always-visible scrollable list (not a dropdown)
 * - Optional grouping with headers
 * - Search functionality
 * - Select All / Unselect All per group
 * - Empty state when disabled
 *
 * Usage:
 * ```html
 * <app-checklist-panel
 *   title="Select States"
 *   subtitle="Filtered by selected regions"
 *   [items]="filteredStates()"
 *   [selectedIds]="selectedStates()"
 *   [groups]="stateGroups()"
 *   [disabled]="selectedRegions().length === 0"
 *   searchPlaceholder="Search states..."
 *   emptyMessage="Please select at least one region first"
 *   (selectionChange)="onStatesChange($event)"
 * />
 * ```
 */
@Component({
  selector: 'app-checklist-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './checklist-panel.component.html',
  styleUrls: ['./checklist-panel.component.scss'],
})
export class ChecklistPanelComponent {
  // ===== INPUTS =====

  /** Panel title */
  title = input<string>('');

  /** Panel subtitle/description */
  subtitle = input<string>('');

  /** Flat list of items (used when no groups provided) */
  items = input<ChecklistItem[]>([]);

  /** Grouped items (takes precedence over flat items) */
  groups = input<ChecklistGroup[]>([]);

  /** Currently selected item IDs */
  selectedIds = input<(number | string)[]>([]);

  /** Whether the panel is disabled */
  disabled = input<boolean>(false);

  /** Search input placeholder */
  searchPlaceholder = input<string>('Search...');

  /** Message shown when disabled */
  emptyMessage = input<string>('No items available');

  /** Icon shown in empty state */
  emptyIcon = input<string>('ti-info-circle');

  /** Maximum height of the list area */
  maxHeight = input<string>('16rem'); // 256px = max-h-64

  /** Accent color for select all button */
  accentColor = input<'blue' | 'orange'>('blue');

  // ===== OUTPUTS =====

  /** Emits the full array of selected IDs when selection changes */
  selectionChange = output<(number | string)[]>();

  // ===== INTERNAL STATE =====

  searchTerm = signal<string>('');

  // ===== COMPUTED =====

  /** Whether we're using grouped mode */
  isGrouped = computed(() => this.groups().length > 0);

  /** Filtered flat items (when not grouped) */
  filteredItems = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const itemList = this.items();
    if (!term) return itemList;
    return itemList.filter(item => item.label.toLowerCase().includes(term));
  });

  /** Filtered groups with filtered items */
  filteredGroups = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const groupList = this.groups();

    if (!term) return groupList;

    return groupList
      .map(group => ({
        ...group,
        items: group.items.filter(item => item.label.toLowerCase().includes(term)),
      }))
      .filter(group => group.items.length > 0);
  });

  /** Check if all items in a group are selected */
  areAllSelectedInGroup(groupId: number | string): boolean {
    const group = this.groups().find(g => g.id === groupId);
    if (!group || group.items.length === 0) return false;
    const selected = this.selectedIds();
    return group.items.every(item => selected.includes(item.id));
  }

  /** Check if a specific item is selected */
  isSelected(itemId: number | string): boolean {
    return this.selectedIds().includes(itemId);
  }

  // ===== METHODS =====

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  toggleItem(itemId: number | string): void {
    const current = [...this.selectedIds()];
    const index = current.indexOf(itemId);

    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(itemId);
    }

    this.selectionChange.emit(current);
  }

  selectAllInGroup(groupId: number | string): void {
    const group = this.groups().find(g => g.id === groupId);
    if (!group) return;

    const groupItemIds = group.items.map(item => item.id);
    const current = this.selectedIds();
    const newSelection = [...new Set([...current, ...groupItemIds])];

    this.selectionChange.emit(newSelection);
  }

  unselectAllInGroup(groupId: number | string): void {
    const group = this.groups().find(g => g.id === groupId);
    if (!group) return;

    const groupItemIds = group.items.map(item => item.id);
    const newSelection = this.selectedIds().filter(id => !groupItemIds.includes(id));

    this.selectionChange.emit(newSelection);
  }

  /** Toggle between select all and unselect all */
  toggleGroupSelection(groupId: number | string): void {
    if (this.areAllSelectedInGroup(groupId)) {
      this.unselectAllInGroup(groupId);
    } else {
      this.selectAllInGroup(groupId);
    }
  }

  /** Get button classes based on accent color */
  getSelectAllButtonClass(allSelected: boolean): string {
    if (allSelected) {
      return 'text-xs font-medium text-red-600 hover:text-red-700';
    }
    const color = this.accentColor();
    return color === 'orange'
      ? 'text-xs font-medium text-orange-600 hover:text-orange-700'
      : 'text-xs font-medium text-blue-600 hover:text-blue-700';
  }
}
